import React from 'react';
import { Link } from 'react-router-dom';

const Features = () => {
  const featureGroups = [
    {
      category: 'AI Search Intelligence',
      color: 'blue',
      features: [
        {
          title: 'AI Search Visibility Score',
          description: 'Get a comprehensive score measuring your presence across ChatGPT, Google AI Overview, Perplexity AI, Bing Chat, Claude, and You.com. Understand exactly how AI search engines see and rank your content.',
          icon: ''
        },
        {
          title: 'Answer Engine Optimization (AEO)',
          description: 'Optimize your content specifically for AI-generated answers. Our proprietary AEO scoring system analyzes how well your content is positioned to be cited in AI responses.',
          icon: ''
        },
        {
          title: 'AI Content Optimizer',
          description: 'Get AI-powered recommendations to improve your content for both traditional and AI search engines. Analyze readability, relevance, entity coverage, and structural optimization.',
          icon: ''
        },
        {
          title: 'Brand Sentiment Analysis',
          description: 'Track how AI search engines perceive and present your brand. Monitor sentiment, accuracy of AI-generated descriptions, and identify opportunities to improve your brand narrative.',
          icon: ''
        }
      ]
    },
    {
      category: 'Traditional SEO Tools',
      color: 'purple',
      features: [
        {
          title: 'SERP Analysis',
          description: 'Deep-dive into search engine results for any keyword. See all 20+ SERP features including featured snippets, knowledge panels, People Also Ask, image packs, video carousels, and more.',
          icon: ''
        },
        {
          title: 'Rank Tracking',
          description: 'Monitor your keyword rankings daily across Google, Bing, and other search engines. Track position changes, visibility trends, and compare performance against competitors.',
          icon: ''
        },
        {
          title: 'Keyword Research',
          description: 'Discover high-value keywords with detailed metrics including search volume, keyword difficulty, CPC data, and SERP feature opportunities. Find content gaps and untapped ranking opportunities.',
          icon: ''
        },
        {
          title: 'Backlink Explorer',
          description: 'Analyze your complete backlink profile. Discover new link opportunities, monitor competitor backlinks, identify toxic links, and track your link-building progress over time.',
          icon: ''
        }
      ]
    },
    {
      category: 'Analysis & Reporting',
      color: 'green',
      features: [
        {
          title: 'Competitor Analysis',
          description: 'Benchmark your SEO performance against competitors. Compare rankings, visibility scores, content strategies, and backlink profiles to identify your competitive advantages and gaps.',
          icon: ''
        },
        {
          title: 'Site Audit',
          description: 'Run comprehensive technical SEO audits to identify issues affecting your search performance. Get prioritized recommendations for fixes including crawlability, speed, schema, and more.',
          icon: ''
        },
        {
          title: 'Content Strategy',
          description: 'Plan your content calendar with data-driven insights. Identify trending topics, content gaps, and optimization opportunities based on what performs best in both traditional and AI search.',
          icon: ''
        },
        {
          title: 'Custom Reports',
          description: 'Generate beautiful, detailed reports for stakeholders and clients. Customize with your branding, schedule automatic delivery, and export in PDF format.',
          icon: ''
        }
      ]
    },
    {
      category: 'Advanced Features',
      color: 'orange',
      features: [
        {
          title: 'Local SEO Rankings',
          description: 'Track your local search visibility across different geographic locations. Monitor Google Business Profile performance, local pack rankings, and location-specific keyword positions.',
          icon: ''
        },
        {
          title: 'Listing Management',
          description: 'Manage and optimize your business listings across multiple directories from one dashboard. Ensure NAP consistency and maximize your local search presence.',
          icon: ''
        },
        {
          title: 'API Access',
          description: 'Integrate SERPfinity data into your own tools and workflows with our comprehensive API. Available on Business and Enterprise plans with flexible rate limits.',
          icon: ''
        },
        {
          title: 'Team Collaboration',
          description: 'Invite team members, assign roles, and collaborate on SEO projects. Share reports, dashboards, and insights across your organization.',
          icon: ''
        }
      ]
    }
  ];

  const colorMap = {
    blue: { bg: 'from-blue-500 to-blue-600', light: 'bg-blue-50 border-blue-100', text: 'text-blue-600' },
    purple: { bg: 'from-purple-500 to-purple-600', light: 'bg-purple-50 border-purple-100', text: 'text-purple-600' },
    green: { bg: 'from-green-500 to-green-600', light: 'bg-green-50 border-green-100', text: 'text-green-600' },
    orange: { bg: 'from-orange-500 to-orange-600', light: 'bg-orange-50 border-orange-100', text: 'text-orange-600' }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Platform Features</h1>
          <p className="text-lg text-blue-200 max-w-2xl mx-auto">
            Everything you need to master AI search visibility and dominate every SERP. From AI-powered analytics to traditional SEO tools.
          </p>
        </div>
      </div>

      {/* Feature Groups */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {featureGroups.map((group, gIdx) => (
          <div key={gIdx} className="mb-20">
            <div className="text-center mb-12">
              <span className={"inline-block px-4 py-1 rounded-full text-sm font-semibold text-white bg-gradient-to-r " + colorMap[group.color].bg + " mb-3"}>
                {group.category}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-8">
              {group.features.map((feature, fIdx) => (
                <div key={fIdx} className={"p-8 rounded-xl border " + colorMap[group.color].light + " hover:shadow-lg transition-shadow"}>
                  <span className="text-4xl mb-4 block">{feature.icon}</span>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* CTA */}
        <div className="text-center py-16 px-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to dominate AI search?</h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Start your free trial today and see how SERPfinity can transform your search visibility.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="inline-block px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition">
              Start Free Trial
            </Link>
            <Link to="/pricing" className="inline-block px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:bg-opacity-10 transition">
              View Pricing
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;
